import { Injectable } from "@angular/core";
import { db, AssertionIssue, ImageType, InspectionJob, SyncableStatus } from "../../../core/database/db";
import { LoggerService } from "../../../shared/services/logger.service";
import { InspectionJobDTO, InspectionJobDTOSyncable } from "../../../domains/jobs/models/InspectionJobDTO";
import { JobIssueDTO, JobIssueDTOSyncable } from "../../../domains/jobIssues/models/IssueDTO";
import { UserVendorContactDTO, UserVendorContactDTOSyncable } from "../../../domains/vendors/models/UserVendorContactDTO";
import { LotAssetDtoSyncable } from "../../../domains/lots/models/LotAssetDto";
import DataSyncMapperService from "./data-sync-mapper.service";
import { AccumulatedResults, BulkUpdateResponse, IssueSyncResponse, LotSyncResponse, SyncResults, UserVendorContactIdentifier } from "../models/SyncResults.model";
import SyncStatusService, { SyncPhase, SyncStatus } from "../sync-status-service";
import { DataSyncApiService } from "./data-sync-api.service";
import { CustomerCareManagerScheduleService } from "../../../domains/jobs/services/customer-care-manager-schedule.service";
import { ConstructionManagerScheduleService } from "../../../domains/jobs/services/construction-manager-schedule.service";
import { AuthService } from "../../authentication/auth.service";
import { CONSTANTS } from "../../../constants";
import { VendorService } from "../../../domains/vendors/services/vendor-service";
import { VendorDTO } from "../../../domains/vendors/models/VendorDTO";

const JOB_DATA_REQUEST_BATCH_SIZE = 3;
const JOB_DATA_REQUEST_BATCH_SIZE_FOR_INDIVIDUAL_SYNC = 1;
const ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE = 10;
const DATABASE_UPDATE_BATCH_SIZE = 20;

export interface BqaUpdateData {
  jobs: InspectionJobDTOSyncable[];
  userVendorContacts: UserVendorContactDTOSyncable[];
  jobIssues: JobIssueDTOSyncable[];
  lotAssets: LotAssetDtoSyncable[];
  estimatedApplyBatchTotal: number;
}


@Injectable({
  providedIn: "root",
})
export default class DataSyncService {
  constructor(
    private logger: LoggerService,
    private dataSyncMapperService: DataSyncMapperService,
    private syncStatusService: SyncStatusService,
    private dataSyncApiService: DataSyncApiService,
    private customerCareManagerScheduleService: CustomerCareManagerScheduleService,
    private constructionManagerScheduleService: ConstructionManagerScheduleService,
    private authService: AuthService,
    private vendorService: VendorService
  ) {
    this.syncTimeout = 1.5 * 60 * 1000;
  }

  private syncTimeout: number | null = null;
  private syncTimeoutId: number | null = null;
  private shouldAbortSync = false;
  private fetchPhaseCompleted = 0;
  private fetchPhaseTotal = 0;
  private sendPhaseCompleted = 0;
  private sendPhaseTotal = 0;

  // Per-entity-type batch progress for the apply phase (e.g. "UpdateDatabaseEntities:Assertions").
  // Averaged with equal weight per type (see updateApplyPhaseProgress) so a type with far more
  // batches than the others (Assertions routinely dwarfs every other type) can't dominate/distort
  // the apply phase's reported progress.
  private applyEntityProgress = new Map<string, { completed: number; total: number }>();

  startUpload = async (areImagesUploading: boolean, shouldPullDataBeforeSyncing: boolean, isForceSync = false, sendJobsIndividually = false): Promise<boolean> => {
    this.shouldAbortSync = false;
    this.syncStatusService.resetPhaseProgress();

    if (isForceSync) {
      // If we're force syncing let's update the InProgress status _immediately_ when they click the button
      this.syncStatusService.setSyncStatus(SyncStatus.InProgress);
    }

    const dataToSync = await this.getDataFromDatabaseToSync(isForceSync);

    const hasJobs = dataToSync.jobs && dataToSync.jobs.length > 0;
    const hasContacts = dataToSync.userVendorContacts && dataToSync.userVendorContacts.length > 0;
    const hasJobIssues = dataToSync.jobIssues && dataToSync.jobIssues.length > 0;
    const hasLotAssets = !!(dataToSync.lotAssets && dataToSync.lotAssets.length > 0);
    const hasDataToUpload = hasJobs || hasContacts || hasJobIssues || hasLotAssets;

    if (hasDataToUpload === false) {
      if (this.syncStatusService.getSyncStatus() === SyncStatus.InProgress && areImagesUploading === false) {
        this.syncStatusService.setSyncStatus(SyncStatus.Complete);
      }
      this.logger.info("[DataSync] No data to sync. Skipping sync.");
      return Promise.resolve(false);
    }

    this.checkForSyncAbort("setSyncStatus(SyncStatus.InProgress)");
    this.syncStatusService.setSyncStatus(SyncStatus.InProgress);

    // Seed all phase totals (fetch/send/apply) as early as possible - before the fetch calls even
    // run - so the blended progress starts representative instead of jumping/diluting once each
    // phase is added or its real batch count becomes known.
    const batchSize = sendJobsIndividually ? JOB_DATA_REQUEST_BATCH_SIZE_FOR_INDIVIDUAL_SYNC : JOB_DATA_REQUEST_BATCH_SIZE;
    const estimatedJobBatches = dataToSync.jobs ? Math.ceil(dataToSync.jobs.length / batchSize) : 0;
    const estimatedIssueBatches = dataToSync.jobIssues ? Math.ceil(dataToSync.jobIssues.length / ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE) : 0;
    const estimatedVendorBatches = dataToSync.userVendorContacts ? Math.ceil(dataToSync.userVendorContacts.length / ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE) : 0;
    const estimatedLotAssetBatches = dataToSync.lotAssets ? Math.ceil(dataToSync.lotAssets.length / ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE) : 0;

    this.sendPhaseCompleted = 0;
    this.sendPhaseTotal = estimatedJobBatches + Math.max(estimatedIssueBatches, estimatedVendorBatches) + estimatedLotAssetBatches;
    this.syncStatusService.updatePhaseProgress("send", this.sendPhaseCompleted, this.sendPhaseTotal);

    this.applyEntityProgress.clear();
    // Seed a single placeholder "type" so the phase reads 0% (not "done", which an empty map would
    // imply) until real per-entity-type totals replace it once updateDatabaseEntityAsBatch runs.
    this.updateApplyPhaseProgress("EstimatedApplyTotal", 0, dataToSync.estimatedApplyBatchTotal || 1);

    const willFetchCustomerCareManagerData = this.authService.isCustomerCareManager() && shouldPullDataBeforeSyncing;
    const willFetchConstructionManagerData = this.authService.isConstructionManager() && shouldPullDataBeforeSyncing;
    this.fetchPhaseCompleted = 0;
    this.fetchPhaseTotal = (willFetchCustomerCareManagerData ? 1 : 0) + (willFetchConstructionManagerData ? 1 : 0);
    if (this.fetchPhaseTotal > 0) {
      this.syncStatusService.updatePhaseProgress("fetch", this.fetchPhaseCompleted, this.fetchPhaseTotal);
    }

    const fetchPhaseStartTime = Date.now();

    this.checkForSyncAbort("fetchAndUpdateCustomerCareManagerData");
    if (this.authService.isCustomerCareManager()) {
      if (shouldPullDataBeforeSyncing) {
        await this.fetchAndUpdateCustomerCareManagerData(isForceSync);
        this.fetchPhaseCompleted++;
        this.syncStatusService.recordCompletedStep("FetchAndUpdateCustomerCareManagerData");
        this.syncStatusService.updatePhaseProgress("fetch", this.fetchPhaseCompleted, this.fetchPhaseTotal);
      } else {
        this.logger.info("[DataSync] shouldPullDataBeforeSyncing is false. Skipping Customer Care Manager data fetch.");
      }
    } else {
      this.logger.info("[DataSync] User is not a Customer Care Manager. Skipping Customer Care Manager data fetch.");
    }

    this.checkForSyncAbort("fetchAndUpdateConstructionManagerData");
    if (this.authService.isConstructionManager()) {
      if (shouldPullDataBeforeSyncing) {
        await this.fetchAndUpdateConstructionManagerData();
        this.fetchPhaseCompleted++;
        this.syncStatusService.recordCompletedStep("FetchAndUpdateConstructionManagerData");
        this.syncStatusService.updatePhaseProgress("fetch", this.fetchPhaseCompleted, this.fetchPhaseTotal);
      } else {
        this.logger.info("[DataSync] shouldPullDataBeforeSyncing is false. Skipping Construction Manager data fetch.");
      }
    } else {
      this.logger.info("[DataSync] User is not a Construction Manager. Skipping Construction Manager data fetch.");
    }

    const fetchPhaseDurationMs = Date.now() - fetchPhaseStartTime;

    this.checkForSyncAbort("SyncManagerSyncStarted");
    return new Promise(async (resolve, reject) => {
      try {
        this.logger.event("DataSync:SyncManagerSyncStarted", {
          isForceSync,
          jobIds: dataToSync.jobs?.map(j => (j as { id: string | number }).id) ?? [],
          jobs: dataToSync.jobs?.length ?? 0,
          userVendorContacts: dataToSync.userVendorContacts?.length ?? 0,
          jobIssues: dataToSync.jobIssues?.length ?? 0
        });

        this.syncStatusService.addSyncProgressItem("DataSync:TotalSyncProcess");

        const shouldStartTimeout = isForceSync === false;
        if (shouldStartTimeout) {
          this.startSyncTimeout(reject);
        }

        const syncStartTime = Date.now();

        this.checkForSyncAbort("sendSyncDataAndProcessResults");
        const sendPhaseStartTime = Date.now();
        const syncApiResults = await this.sendSyncDataAndProcessResults(dataToSync, isForceSync, sendJobsIndividually);
        const sendPhaseDurationMs = Date.now() - sendPhaseStartTime;

        this.checkForSyncAbort("updateSyncedDatabaseEntitiesWithResults");
        const applyPhaseStartTime = Date.now();
        await this.updateSyncedDatabaseEntitiesWithResults(syncApiResults);
        const applyPhaseDurationMs = Date.now() - applyPhaseStartTime;

        const phaseDurations: Partial<Record<SyncPhase, number>> = { send: sendPhaseDurationMs, apply: applyPhaseDurationMs };
        if (this.fetchPhaseTotal > 0) {
          phaseDurations.fetch = fetchPhaseDurationMs;
        }
        this.syncStatusService.recordPhaseDurations(phaseDurations);

        const syncEndTime = Date.now();
        this.logger.event("DataSync:SyncManagerSyncCompleted", {
          isForceSync,
          duration: `${syncEndTime - syncStartTime}ms`
        });

        if (shouldStartTimeout) {
          this.stopSyncTimeout();
        }
        this.syncStatusService.markSyncProgressItemComplete("DataSync:TotalSyncProcess");

        resolve(true);
      } catch (error) {
        reject(error);
      }
    });
  };

  fetchAndUpdateCustomerCareManagerData = async (isForceSync: boolean): Promise<void> => {
    if (navigator.onLine === false) {
      this.logger.info("[DataSync] Skipping fetch and update for Customer Care Manager data because the device is offline.");
      return;
    }

    try {
      this.logger.event("DataSync:FetchAndUpdateCustomerCareManagerDataStarted", { isForceSync });

      this.logger.timeStart("DataSync.GetCustomerCareManagerDataForSyncApi");
      const serverData = await this.customerCareManagerScheduleService.getCustomerCareManagerDataForSync();
      this.logger.timeEnd("DataSync.GetCustomerCareManagerDataForSyncApi");

      this.logger.timeStart("DataSync.CustomerCareManagerSyncServerData");
      await this.customerCareManagerScheduleService.syncServerData(serverData, false);
      this.logger.timeEnd("DataSync.CustomerCareManagerSyncServerData");

    } catch (error) {
      this.logger.error("[DataSync] Error fetching and updating Customer Care Manager data", error);
      throw error;
    }
  };

  fetchAndUpdateConstructionManagerData = async (lotIdFilter: number[] = []): Promise<boolean> => {
    if (navigator.onLine === false) {
      this.logger.info("[DataSync] Skipping fetch and update for Construction Manager data because the device is offline.");
      return false;
    }

    try {
      this.logger.event("DataSync:FetchAndUpdateConstructionManagerData");
      this.logger.timeStart("DataSync.GetConstructionManagerDataForSyncApi");
      const serverData = await this.constructionManagerScheduleService.getConstructionManagerDataForSync(lotIdFilter);
      this.logger.timeEnd("DataSync.GetConstructionManagerDataForSyncApi");

      let userVendorContacts: UserVendorContactDTO[] = [];
      let vendors: VendorDTO[] | null = null;

      if (lotIdFilter.length > 0) {
        vendors = await this.vendorService.getVendorsForLotIds(lotIdFilter);
      } else {
        vendors = await this.vendorService.getVendorsForAllLots();
      }

      if (vendors) {
        userVendorContacts = vendors.flatMap(v => v.userVendorContacts ?? []);
      }

      this.logger.timeStart("DataSync.ConstructionManagerSyncServerData");
      await this.constructionManagerScheduleService.syncServerData(serverData, userVendorContacts, false);
      const lotWasntLoadedToFetchVendors = vendors === null;
      if (lotWasntLoadedToFetchVendors) {
        if (lotIdFilter.length > 0) {
          vendors = await this.vendorService.getVendorsForLotIds(lotIdFilter);
        } else {
          vendors = await this.vendorService.getVendorsForAllLots();
        }
        userVendorContacts = (vendors ?? []).flatMap(v => v.userVendorContacts ?? []);
        await this.constructionManagerScheduleService.syncUserVendorContactData(userVendorContacts);
        await this.constructionManagerScheduleService.cleanupUserVendorContacts(userVendorContacts);
      }
      this.logger.timeEnd("DataSync.ConstructionManagerSyncServerData");
      return true;
    } catch (error) {
      this.logger.error("[DataSync] Error fetching and updating Construction Manager data", error);
      throw error;
    }
  };

  async getDataFromDatabaseToSync(isForceSync = false): Promise<BqaUpdateData> {
    this.logger.timeStart("DataSync.GetDataFromDatabaseToSync");
    const syncStatusFilter = isForceSync ? [SyncableStatus.Pending, SyncableStatus.Completed] : [SyncableStatus.Pending];

    // NOTE: deliberately not wrapped in an explicit db.transaction(). Dexie's transaction zone follows
    // the async continuation chain (not just the callback body) and IndexedDB auto-commits a transaction
    // once it goes idle for a tick, so a long-lived transaction spanning this many sequential awaits can
    // leak into (and break) unrelated concurrent Dexie calls elsewhere in the app. Each query below just
    // gets its own short-lived implicit transaction instead, as before.

    // ------------------ JOB SIGNATURES / USER VENDOR CONTACTS / JOB ISSUE VENDORS / MEDIA / ASSERTIONISSUES / JOB ISSUES
    // Independent of each other (only depend on syncStatusFilter), so run concurrently instead of one after another.
    const [jobSignatures, userVendorContacts, jobIssueVendors, syncStatusMedias, syncStatusAssertionIssues, syncStatusJobIssues] = await Promise.all([
      db.jobSignature
        .where("syncStatus").anyOf(syncStatusFilter)
        .toArray(),
      db.userVendorContact
        .where("syncStatus").anyOf(syncStatusFilter)
        .toArray(),
      db.jobIssueVendor
        .where("syncStatus").anyOf(syncStatusFilter)
        .toArray(),
      db.media
        .where("syncStatus").anyOf(syncStatusFilter)
        .toArray(),
      db.assertionIssue
        .where("syncStatus").anyOf(syncStatusFilter)
        .toArray(),
      db.jobIssue
        .where("syncStatus").anyOf(syncStatusFilter)
        .toArray()
    ]);

    // ------------------ MEDIA / SOFT-DELETED MEDIA (single scan, partitioned instead of scanning "media" by syncStatus twice)
    const medias = syncStatusMedias.filter(media => media.deleted !== 1).map(media => {
      delete media.base64Image; // Remove base64 data to reduce payload size
      return media;
    });
    const softDeletedMedias = syncStatusMedias.filter(media => media.deleted === 1).map(media => {
      delete media.base64Image; // Remove base64 data to reduce payload size
      return media;
    });

    // Jobs whose own record changed, or whose signature changed. Kicked off now (only depends on
    // jobSignatures above) so it runs alongside the structural cascade below instead of after it.
    let jobsFromOwnChangesQuery = db.inspectionJob.where("syncStatus").anyOf(syncStatusFilter);
    if (jobSignatures.length > 0) {
      jobsFromOwnChangesQuery = jobsFromOwnChangesQuery.or("id").anyOf(jobSignatures.map(signature => signature.jobId));
    }
    const jobsFromOwnChangesPromise = jobsFromOwnChangesQuery.toArray();

    // ------------------ ASSERTIONISSUES / SOFT-DELETED ASSERTIONISSUES (single syncStatus scan, partitioned)
    const activeAssertionIssuesById = new Map(syncStatusAssertionIssues.filter(issue => issue.deleted !== 1).map(issue => [issue.id!, issue]));
    const softDeletedAssertionIssues = syncStatusAssertionIssues.filter(issue => issue.deleted === 1);

    const assertionIssueParentIdentifiers = [...medias, ...softDeletedMedias].map(media => media.parentIdentifier);
    if (assertionIssueParentIdentifiers.length > 0) {
      const identifierMatchedAssertionIssues = await db.assertionIssue
        .where("identifier").anyOf(assertionIssueParentIdentifiers)
        .toArray();
      identifierMatchedAssertionIssues.forEach(issue => activeAssertionIssuesById.set(issue.id!, issue));
    }
    const assertionIssues = Array.from(activeAssertionIssuesById.values());

    // ------------------ JOB ISSUES / SOFT-DELETED JOB ISSUES (single syncStatus scan, partitioned)
    const activeJobIssuesById = new Map(syncStatusJobIssues.filter(issue => issue.deleted !== 1).map(issue => [issue.id!, issue]));
    const softDeletedJobIssues = syncStatusJobIssues.filter(issue => issue.deleted === 1);

    const jobIssueIdentifiers = [
      ...medias.map(media => media.parentIdentifier),
      ...softDeletedMedias.map(media => media.parentIdentifier),
      ...jobIssueVendors.map(vendor => vendor.issueIdentifier)
    ];
    if (jobIssueIdentifiers.length > 0) {
      const identifierMatchedJobIssues = await db.jobIssue
        .where("identifier").anyOf(jobIssueIdentifiers)
        .toArray();
      identifierMatchedJobIssues.forEach(issue => activeJobIssuesById.set(issue.id!, issue));
    }
    const jobIssues = Array.from(activeJobIssuesById.values());

    // ------------------ ASSERTIONS
    let assertionsQuery = db.assertion.where("syncStatus").anyOf(syncStatusFilter);

    if (medias.length > 0) {
      assertionsQuery = assertionsQuery.or("identifier").anyOf(medias.map(media => media.parentIdentifier));
    }
    if (assertionIssues.length > 0) {
      assertionsQuery = assertionsQuery.or("identifier").anyOf(assertionIssues.map(issue => issue.assertionIdentifier));
    }
    const assertions = await assertionsQuery.toArray();

    // ------------------ COMPONENTS
    let componentsQuery = db.component.where("syncStatus").anyOf(syncStatusFilter);
    if (assertions.length > 0) {
      componentsQuery = componentsQuery.or("id").anyOf(assertions.map(assertion => assertion.componentId));
    }
    const components = await componentsQuery.toArray();

    // ------------------ AREAS
    let areasQuery = db.area.where("syncStatus").anyOf(syncStatusFilter);
    if (components.length > 0) {
      areasQuery = areasQuery.or("id").anyOf(components.map(component => component.areaId));
    }
    const areas = await areasQuery.toArray();

    // ------------------ ZONES
    let zonesQuery = db.zone.where("syncStatus").anyOf(syncStatusFilter);
    if (areas.length > 0) {
      zonesQuery = zonesQuery.or("id").anyOf(areas.map(area => area.zoneId));
    }
    const zones = await zonesQuery.toArray();

    // ------------------ JOBS
    // Jobs that need to be resent because a lot-scoped area/component/assertion changed under them.
    // An area's buildQualityStepScope pins it to exactly one job on the lot, EXCEPT an undefined/null
    // scope, which means the area is shared between the BQ6 and BQ7 jobs on that lot. Using this scope
    // (rather than just the lot) avoids pulling in every other job on the lot (there can be up to 9).
    const zonesById = new Map(zones.map(zone => [zone.id, zone]));
    const lotIdsByBuildQualityStep = new Map<number, Set<number>>();
    const universalScopeLotIds = new Set<number>();
    for (const area of areas) {
      const zone = zonesById.get(area.zoneId);
      if (!zone) {
        continue;
      }
      if (area.buildQualityStepScope === undefined || area.buildQualityStepScope === null) {
        universalScopeLotIds.add(zone.lotId);
      } else {
        const lotIds = lotIdsByBuildQualityStep.get(area.buildQualityStepScope) ?? new Set<number>();
        lotIds.add(zone.lotId);
        lotIdsByBuildQualityStep.set(area.buildQualityStepScope, lotIds);
      }
    }

    let jobsFromStructuralChanges: InspectionJob[] = [];
    const candidateLotIds = new Set<number>(universalScopeLotIds);
    lotIdsByBuildQualityStep.forEach(lotIds => lotIds.forEach(lotId => candidateLotIds.add(lotId)));
    if (candidateLotIds.size > 0) {
      const universalBuildQualitySteps: number[] = [CONSTANTS.BUILD_QUALITY_STEPS.BQ6, CONSTANTS.BUILD_QUALITY_STEPS.BQ7];
      const candidateJobs = await db.inspectionJob
        .where("lotId").anyOf(Array.from(candidateLotIds))
        .toArray();
      jobsFromStructuralChanges = candidateJobs.filter(job =>
        lotIdsByBuildQualityStep.get(job.buildQualityStep)?.has(job.lotId)
        || (universalScopeLotIds.has(job.lotId) && universalBuildQualitySteps.includes(job.buildQualityStep))
      );
    }

    const jobsFromOwnChanges = await jobsFromOwnChangesPromise;
    const jobsById = new Map<number, InspectionJob>();
    for (const job of [...jobsFromOwnChanges, ...jobsFromStructuralChanges]) {
      jobsById.set(job.id, job);
    }
    const jobs = Array.from(jobsById.values());

    const allMedia = [...medias, ...softDeletedMedias];
    const allAssertionIssues = [...assertionIssues, ...softDeletedAssertionIssues];
    const allJobIssues = [...jobIssues, ...softDeletedJobIssues];

    // ------------------ LOT PHOTO ASSETS
    // Lots join media via their int id (parentIdentifier), not a guid identifier like other entities.
    const lotPhotoMedia = allMedia.filter(media => media.imageType === ImageType.LotPhoto);
    const lotIdsWithPhotoChanges = Array.from(new Set(lotPhotoMedia.map(media => Number(media.parentIdentifier))));
    const lotsWithPhotoChanges = lotIdsWithPhotoChanges.length > 0
      ? await db.lot.where("id").anyOf(lotIdsWithPhotoChanges)
        .toArray()
      : [];
    const mappedLotAssets = lotsWithPhotoChanges.flatMap(lot => this.dataSyncMapperService.mapLot(lot, lotPhotoMedia));

    const mappedJobIssues = allJobIssues.map<JobIssueDTOSyncable>(jobIssue => this.dataSyncMapperService.mapJobIssue(jobIssue, allMedia, jobIssueVendors));
    const mappedVendorContacts = userVendorContacts.map(this.dataSyncMapperService.mapVendorContact);
    const mappedJob = jobs.map(job => this.dataSyncMapperService.mapJob(
      job,
      jobSignatures,
      jobIssues,
      zones,
      areas,
      components,
      assertions,
      allAssertionIssues,
      allMedia
    ));

    // Rough pre-estimate of how many DATABASE_UPDATE_BATCH_SIZE-sized batches the apply phase will
    // need, based on locally known dirty-record counts. Only used to seed a placeholder "not started
    // yet" total before real per-entity-type totals are known (see updateApplyPhaseProgress).
    const estimatedApplyBatchTotal = [
      jobs.length,
      areas.length,
      components.length,
      assertions.length,
      allAssertionIssues.length,
      allMedia.length,
      allJobIssues.length,
      jobIssueVendors.length,
      jobSignatures.length,
      userVendorContacts.length,
      mappedLotAssets.length
    ].reduce((total, count) => total + Math.ceil(count / DATABASE_UPDATE_BATCH_SIZE), 0);

    const updateData: BqaUpdateData = {
      jobIssues: mappedJobIssues,
      userVendorContacts: mappedVendorContacts,
      jobs: mappedJob,
      lotAssets: mappedLotAssets,
      estimatedApplyBatchTotal
    };

    this.logger.timeEnd("DataSync.GetDataFromDatabaseToSync");
    return updateData;
  }

  private startSyncTimeout(reject: (reason?: Error) => void) {
    if (this.syncTimeoutId) {
      clearTimeout(this.syncTimeoutId);
    }

    this.syncTimeoutId = window.setTimeout(() => {
      reject(new Error("Data sync operation timed out"));
    }, this.syncTimeout || 30000);
  }

  private stopSyncTimeout() {
    if (this.syncTimeoutId) {
      clearTimeout(this.syncTimeoutId);
    }
  }

  private async sendSyncDataAndProcessResults(data: BqaUpdateData, isForceSync: boolean, sendJobsIndividually: boolean) {
    try {
      this.logger.event("DataSync:SendSyncDataAndProcessResultsStarted", { isForceSync, timeout: this.syncTimeout, jobIds: data.jobs?.map(j => j.id) });

      let syncApiResults: AccumulatedResults = {
        jobIds: [],
        areaIdentifiers: [],
        componentIdentifiers: [],
        assertionIdentifiers: [],
        issueIdentifiers: [],
        mediaIdentifiers: [],
        contactResultSet: [],
        signatureIdentifiers: [],
        jobIssueIdentifiers: [],
        jobIssueMediaIdentifiers: [],
        jobIssueVendorIdentifiers: [],
        deletedJobIssueVendorIdentifiers: [],
        deletedJobIssueMediaIdentifiers: [],
        deletedUserVendorContactIdentifiers: [],
        deletedAssertionIssueIdentifiers: [],
        deletedAssertionMediaIdentifiers: [],
        deletedJobIssueIdentifiers: [],
        lotAssetMediaIdentifiers: [],
        deletedLotAssetMediaIdentifiers: [],
        alreadyCompletedJobIds: []
      };

      const batchSize = sendJobsIndividually ? JOB_DATA_REQUEST_BATCH_SIZE_FOR_INDIVIDUAL_SYNC : JOB_DATA_REQUEST_BATCH_SIZE;

      const jobBatches = data.jobs ? Array.from({ length: Math.ceil(data.jobs.length / batchSize) }, (_, i) => data.jobs.slice(i * batchSize, i * batchSize + batchSize)) : [];
      let issueBatches = data.jobIssues ? Array.from({ length: Math.ceil(data.jobIssues.length / ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE) }, (_, i) => data.jobIssues.slice(i * ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE, i * ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE + ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE)) : [];
      const vendorContactBatches = data.userVendorContacts ? Array.from({ length: Math.ceil(data.userVendorContacts.length / ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE) }, (_, i) => data.userVendorContacts.slice(i * ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE, i * ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE + ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE)) : [];
      const lotAssetBatches = data.lotAssets ? Array.from({ length: Math.ceil(data.lotAssets.length / ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE) }, (_, i) => data.lotAssets.slice(i * ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE, i * ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE + ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE)) : [];

      // Reconcile the send-phase total against the real batch counts (should normally already match
      // the upfront estimate seeded in startUpload, but never let the total shrink mid-run).
      const recomputedSendTotal = jobBatches.length + Math.max(issueBatches.length, vendorContactBatches.length) + lotAssetBatches.length;
      this.sendPhaseTotal = Math.max(this.sendPhaseTotal, recomputedSendTotal);
      this.syncStatusService.updatePhaseProgress("send", this.sendPhaseCompleted, this.sendPhaseTotal);

      const failedJobs: InspectionJobDTO[] = [];
      for (let i = 0; i < jobBatches.length; i++) {
        const currentBatchIndex = i + 1;
        const jobBatch = jobBatches[i];

        try {
          this.logger.event("DataSync:ProcessingJobBatch", { jobIds: jobBatch?.map(j => j.id), currentBatch: currentBatchIndex, batchSize: jobBatch.length, totalBatches: jobBatches.length });
          syncApiResults = await this.sendJobData(jobBatch, syncApiResults);
        } catch (error) {
          this.logger.error(`[DataSync] Error syncing job batch ${currentBatchIndex} of ${jobBatches.length}`, error);
          failedJobs.push(...jobBatch);
        } finally {
          this.sendPhaseCompleted++;
          this.syncStatusService.recordCompletedStep(`JobSync:Batch${currentBatchIndex}`);
          this.syncStatusService.updatePhaseProgress("send", this.sendPhaseCompleted, this.sendPhaseTotal);
        }
      }

      // An issue whose job batch just failed may reference a room/area that never made it to the
      // server, so sending it now would trip FK_JobIssue_LotArea. Hold it back until the job is
      // retried individually below, instead of sending it alongside the unaffected jobs' issues.
      const failedJobIds = new Set(failedJobs.map(j => j.id));
      const deferredJobIssues = data.jobIssues ? data.jobIssues.filter(issue => failedJobIds.has(issue.jobId)) : [];
      if (deferredJobIssues.length > 0) {
        const originalIssueBatchCount = issueBatches.length;
        const issuesReadyToSend = data.jobIssues.filter(issue => !failedJobIds.has(issue.jobId));
        issueBatches = Array.from({ length: Math.ceil(issuesReadyToSend.length / ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE) }, (_, i) => issuesReadyToSend.slice(i * ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE, i * ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE + ISSUE_AND_VENDOR_DATA_REQUEST_BATCH_SIZE));

        // The upfront total assumed the pre-deferral batch count; shrink it back down (never below
        // what's already completed) so the retry pass's own total bump below doesn't double-count.
        const removedBatchSteps = originalIssueBatchCount - issueBatches.length;
        this.sendPhaseTotal = Math.max(this.sendPhaseCompleted, this.sendPhaseTotal - removedBatchSteps);

        this.logger.event("DataSync:DeferringJobIssuesForFailedJobs", { jobIds: Array.from(failedJobIds), issueIdentifiers: deferredJobIssues.map(i => i.identifier) });
      }

      const failedJobIssues: JobIssueDTO[] = [];
      const failedVendorContacts: UserVendorContactDTO[] = [];
      for (let i = 0; i < Math.max(issueBatches.length, vendorContactBatches.length); i++) {
        const currentBatchIndex = i + 1;
        this.logger.event("DataSync:ProcessingIssueAndVendorBatch", { issueIds: issueBatches[i]?.map(j => j.identifier), vendorContactIds: vendorContactBatches[i]?.map(v => v.id), currentBatch: currentBatchIndex, totalBatches: Math.max(issueBatches.length, vendorContactBatches.length) });

        const issueBatch = issueBatches[i] || [];
        const vendorContactBatch = vendorContactBatches[i] || [];

        try {
          syncApiResults = await this.sendIssueAndVendorData(issueBatch, vendorContactBatch, syncApiResults);
        } catch (error) {
          this.logger.error(`[DataSync] Error syncing issue and vendor batch ${currentBatchIndex} of ${Math.max(issueBatches.length, vendorContactBatches.length)}`, error);
          failedJobIssues.push(...issueBatch);
          failedVendorContacts.push(...vendorContactBatch);
        } finally {
          this.sendPhaseCompleted++;
          this.syncStatusService.recordCompletedStep(`IssueAndVendorSync:Batch${currentBatchIndex}`);
          this.syncStatusService.updatePhaseProgress("send", this.sendPhaseCompleted, this.sendPhaseTotal);
        }
      }

      const failedLotAssets: LotAssetDtoSyncable[] = [];
      for (let i = 0; i < lotAssetBatches.length; i++) {
        const currentBatchIndex = i + 1;
        const lotAssetBatch = lotAssetBatches[i];

        try {
          this.logger.event("DataSync:ProcessingLotAssetBatch", { lotIds: lotAssetBatch?.map(a => a.lotId), currentBatch: currentBatchIndex, totalBatches: lotAssetBatches.length });
          syncApiResults = await this.sendLotAssetData(lotAssetBatch, syncApiResults);
        } catch (error) {
          this.logger.error(`[DataSync] Error syncing lot asset batch ${currentBatchIndex} of ${lotAssetBatches.length}`, error);
          failedLotAssets.push(...lotAssetBatch);
        } finally {
          this.sendPhaseCompleted++;
          this.syncStatusService.recordCompletedStep(`LotAssetSync:Batch${currentBatchIndex}`);
          this.syncStatusService.updatePhaseProgress("send", this.sendPhaseCompleted, this.sendPhaseTotal);
        }
      }

      // Isolate a single bad record from the rest of its batch by retrying failed batches one record at a time.
      if (failedJobs.length > 0) {
        syncApiResults = await this.retryFailedJobsIndividually(failedJobs, syncApiResults);
      }

      // Retry deferred issues after their job, so a now-successful job retry has already created
      // the LotArea rows they depend on; if the job retry still failed, these will fail the same
      // way and remain Pending for the next sync, same as any other unresolved individual failure.
      failedJobIssues.push(...deferredJobIssues);

      if (failedJobIssues.length > 0 || failedVendorContacts.length > 0) {
        syncApiResults = await this.retryFailedIssuesAndVendorContactsIndividually(failedJobIssues, failedVendorContacts, syncApiResults);
      }
      if (failedLotAssets.length > 0) {
        syncApiResults = await this.retryFailedLotAssetsIndividually(failedLotAssets, syncApiResults);
      }

      this.logger.event("DataSync:SendSyncDataAndProcessResultsCompleted", syncApiResults);

      return syncApiResults;

    } catch (error) {
      this.logger.error(`[DataSync] Error syncing data for jobs: ${data.jobs?.map(j => j.id).join(",")}`, error);

      if (this.syncTimeoutId) {
        clearTimeout(this.syncTimeoutId);
      }
      throw error;
    }
  }

  private async sendJobData(jobs: InspectionJobDTO[], syncApiResults: AccumulatedResults) {
    if (jobs) {
      this.logger.event("DataSync:sendJobDataStarted", { jobs: jobs?.map(x => x.id) });

      this.logger.timeStart("DataSync.BulkUpdateJobsApi");
      const result = await this.dataSyncApiService.bulkUpdateJobs(jobs);
      this.logger.timeEnd("DataSync.BulkUpdateJobsApi");

      this.logger.event("DataSync:sendJobDataCompleted", result);

      const jobsSyncResults: BulkUpdateResponse = {
        jobIds: result.jobIds || [],
        areaIdentifiers: result.areaIdentifiers || [],
        componentIdentifiers: result.componentIdentifiers || [],
        assertionIdentifiers: result.assertionIdentifiers || [],
        issueIdentifiers: result.issueIdentifiers || [],
        mediaIdentifiers: result.mediaIdentifiers || [],
        signatureIdentifiers: result.signatureIdentifiers || [],
        deletedJobIssueMediaIdentifiers: result.deletedJobIssueMediaIdentifiers || [],
        deletedAssertionIssueIdentifiers: result.deletedAssertionIssueIdentifiers || [],
        deletedAssertionMediaIdentifiers: result.deletedAssertionMediaIdentifiers || [],
        alreadyCompletedJobIds: result.alreadyCompletedJobIds || []
      };


      return this.mergeSyncResults(syncApiResults, jobsSyncResults);
    }

    this.logger.event("DataSync:sendJobData:noJobsToSync", { message: "No jobs provided for sync." });
    return syncApiResults;
  }

  private async sendIssueAndVendorData(jobIssues: JobIssueDTO[], userVendorContacts: UserVendorContactDTO[], syncApiResults: AccumulatedResults) {
    if (jobIssues || userVendorContacts) {
      this.logger.event("DataSync:sendIssueAndVendorDataStarted", { jobIssues: jobIssues?.map(x => x.identifier) });

      this.logger.timeStart("DataSync.SyncIssuesApi");
      const result = await this.dataSyncApiService.syncIssues(userVendorContacts, jobIssues);
      this.logger.timeEnd("DataSync.SyncIssuesApi");

      this.logger.event("DataSync:sendIssueAndVendorDataCompleted", result);

      const issuesAndVendorsResults: IssueSyncResponse = {
        contactResultSet: result.contactResultSet || [],
        jobIssueIdentifiers: result.jobIssueIdentifiers || [],
        jobIssueMediaIdentifiers: result.jobIssueMediaIdentifiers || [],
        jobIssueVendorIdentifiers: result.jobIssueVendorIdentifiers || [],
        deletedJobIssueVendorIdentifiers: result.deletedJobIssueVendorIdentifiers || [],
        deletedJobIssueMediaIdentifiers: result.deletedJobIssueMediaIdentifiers || [],
        deletedUserVendorContactIdentifiers: result.deletedUserVendorContactIdentifiers || [],
        deletedJobIssueIdentifiers: result.deletedJobIssueIdentifiers || []
      };

      return this.mergeSyncResults(syncApiResults, issuesAndVendorsResults);
    }

    this.logger.event("DataSync:sendIssueAndVendorData:noIssuesToSync", { message: "No issues provided for sync." });
    return syncApiResults;
  }

  private async sendLotAssetData(lotAssets: LotAssetDtoSyncable[], syncApiResults: AccumulatedResults) {
    if (lotAssets.length > 0) {
      this.logger.event("DataSync:sendLotAssetDataStarted", { lotIds: lotAssets.map(a => a.lotId) });

      this.logger.timeStart("DataSync.SyncLotApi");
      const result = await this.dataSyncApiService.syncLot(lotAssets);
      this.logger.timeEnd("DataSync.SyncLotApi");

      this.logger.event("DataSync:sendLotAssetDataCompleted", result);

      const lotAssetResults: LotSyncResponse = {
        lotAssetMediaIdentifiers: result.lotAssetMediaIdentifiers || [],
        deletedLotAssetMediaIdentifiers: result.deletedLotAssetMediaIdentifiers || []
      };

      return this.mergeSyncResults(syncApiResults, lotAssetResults);
    }

    this.logger.event("DataSync:sendLotAssetData:noLotAssetsToSync", { message: "No lot assets provided for sync." });
    return syncApiResults;
  }

  private async retryFailedJobsIndividually(failedJobs: InspectionJobDTO[], accumulatedSyncApiResults: AccumulatedResults) {
    this.logger.event("DataSync:RetryingFailedJobsIndividually", { jobIds: failedJobs.map(j => j.id), count: failedJobs.length });

    this.sendPhaseTotal += failedJobs.length;
    this.syncStatusService.updatePhaseProgress("send", this.sendPhaseCompleted, this.sendPhaseTotal);

    let syncApiResults = accumulatedSyncApiResults;
    for (const job of failedJobs) {
      try {
        syncApiResults = await this.sendJobData([job], syncApiResults);
      } catch (error) {
        this.logger.error(`[DataSync] Error re-syncing job ${job.id} individually after its batch failed`, error);
      } finally {
        this.sendPhaseCompleted++;
        this.syncStatusService.recordCompletedStep(`JobSync:Retry:${job.id}`);
        this.syncStatusService.updatePhaseProgress("send", this.sendPhaseCompleted, this.sendPhaseTotal);
      }
    }

    return syncApiResults;
  }

  private async retryFailedIssuesAndVendorContactsIndividually(failedJobIssues: JobIssueDTO[], failedVendorContacts: UserVendorContactDTO[], accumulatedSyncApiResults: AccumulatedResults) {
    this.logger.event("DataSync:RetryingFailedIssuesAndVendorContactsIndividually", {
      issueIdentifiers: failedJobIssues.map(i => i.identifier),
      vendorContactIds: failedVendorContacts.map(v => v.id),
      issueCount: failedJobIssues.length,
      vendorContactCount: failedVendorContacts.length
    });

    let syncApiResults = accumulatedSyncApiResults;

    this.sendPhaseTotal += failedJobIssues.length + failedVendorContacts.length;
    this.syncStatusService.updatePhaseProgress("send", this.sendPhaseCompleted, this.sendPhaseTotal);

    // Issues and vendor contacts are unrelated entities batched together only for pacing, so retry each independently.
    for (const jobIssue of failedJobIssues) {
      try {
        syncApiResults = await this.sendIssueAndVendorData([jobIssue], [], syncApiResults);
      } catch (error) {
        this.logger.error(`[DataSync] Error re-syncing issue ${jobIssue.identifier} individually after its batch failed`, error);
      } finally {
        this.sendPhaseCompleted++;
        this.syncStatusService.recordCompletedStep(`IssueAndVendorSync:Retry:Issue:${jobIssue.identifier}`);
        this.syncStatusService.updatePhaseProgress("send", this.sendPhaseCompleted, this.sendPhaseTotal);
      }
    }

    for (const vendorContact of failedVendorContacts) {
      try {
        syncApiResults = await this.sendIssueAndVendorData([], [vendorContact], syncApiResults);
      } catch (error) {
        this.logger.error(`[DataSync] Error re-syncing vendor contact ${vendorContact.id} individually after its batch failed`, error);
      } finally {
        this.sendPhaseCompleted++;
        this.syncStatusService.recordCompletedStep(`IssueAndVendorSync:Retry:Vendor:${vendorContact.id}`);
        this.syncStatusService.updatePhaseProgress("send", this.sendPhaseCompleted, this.sendPhaseTotal);
      }
    }

    return syncApiResults;
  }

  private async retryFailedLotAssetsIndividually(failedLotAssets: LotAssetDtoSyncable[], accumulatedSyncApiResults: AccumulatedResults) {
    this.logger.event("DataSync:RetryingFailedLotAssetsIndividually", { lotIds: failedLotAssets.map(a => a.lotId), count: failedLotAssets.length });

    this.sendPhaseTotal += failedLotAssets.length;
    this.syncStatusService.updatePhaseProgress("send", this.sendPhaseCompleted, this.sendPhaseTotal);

    let syncApiResults = accumulatedSyncApiResults;
    for (const lotAsset of failedLotAssets) {
      try {
        syncApiResults = await this.sendLotAssetData([lotAsset], syncApiResults);
      } catch (error) {
        this.logger.error(`[DataSync] Error re-syncing lot asset for lot ${lotAsset.lotId} individually after its batch failed`, error);
      } finally {
        this.sendPhaseCompleted++;
        this.syncStatusService.recordCompletedStep(`LotAssetSync:Retry:${lotAsset.lotId}`);
        this.syncStatusService.updatePhaseProgress("send", this.sendPhaseCompleted, this.sendPhaseTotal);
      }
    }

    return syncApiResults;
  }

  public async updateSyncedDatabaseEntitiesWithResults(updatedIdentifiers: AccumulatedResults) {
    this.logger.event("DataSync:updateSyncedDatabaseEntitiesWithResultsStarted", { jobIds: updatedIdentifiers?.jobIds ?? [] });
    this.logger.timeStart("DataSync.UpdateSyncedDatabaseEntitiesWithResults");
    const {
      jobIds,
      alreadyCompletedJobIds,
      areaIdentifiers,
      componentIdentifiers,
      assertionIdentifiers,
      issueIdentifiers,
      mediaIdentifiers,
      contactResultSet,
      signatureIdentifiers,
      jobIssueIdentifiers,
      jobIssueMediaIdentifiers,
      jobIssueVendorIdentifiers,
      deletedJobIssueVendorIdentifiers,
      deletedJobIssueMediaIdentifiers,
      deletedUserVendorContactIdentifiers,
      deletedAssertionIssueIdentifiers,
      deletedAssertionMediaIdentifiers,
      deletedJobIssueIdentifiers,
      lotAssetMediaIdentifiers,
      deletedLotAssetMediaIdentifiers,
    } = updatedIdentifiers;

    await db.transaction("readwrite", [
      "syncStatus",
      "inspectionJob",
      "area",
      "component",
      "assertion",
      "assertionIssue",
      "media",
      "userVendorContact",
      "jobIssue",
      "jobIssueVendor",
      "jobSignature",
      "lot"
    ], async () => {
      db.setSuppressHooks(true);
      try {

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:Jobs");
        await this.updateDatabaseEntityAsBatch<number>("UpdateDatabaseEntities:Jobs", jobIds, async (jobIds: number[]) => {
          await db.inspectionJob.bulkUpdate(jobIds.map(id => ({
            key: id,
            changes: {
              syncStatus: SyncableStatus.Completed,
            }
          })));
        });

        // The server refused these because it already has them submitted. Clearing pending is what lets a
        // later schedule pull reconcile them at all - left pending they are skipped by both merge and cleanup.
        if (alreadyCompletedJobIds?.length) {
          this.logger.warn(
            `[DataSync] Server already has jobs [${alreadyCompletedJobIds.join(", ")}] submitted; discarding local changes and adopting server state.`
          );
          await this.updateDatabaseEntityAsBatch<number>("UpdateDatabaseEntities:AlreadyCompletedJobs", alreadyCompletedJobIds, async (ids: number[]) => {
            await db.inspectionJob.bulkUpdate(ids.map(id => ({
              key: id,
              changes: {
                isComplete: true,
                syncStatus: SyncableStatus.Completed,
              }
            })));
          });
        }

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:Areas");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:Areas", areaIdentifiers, async (areaIdentifiers: string[]) => {
          await db.area.where("identifier").anyOf(areaIdentifiers)
            .modify(item => {
              item.syncStatus = SyncableStatus.Completed;
              item.updatedAt = new Date(new Date(item.updatedAt).getTime() + 1);
            });
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:Components");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:Components", componentIdentifiers, async (componentIdentifiers: string[]) => {
          await db.component.where("identifier").anyOf(componentIdentifiers)
            .modify(item => {
              item.syncStatus = SyncableStatus.Completed;
              item.updatedAt = new Date(new Date(item.updatedAt).getTime() + 1);
            });
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:Assertions");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:Assertions", assertionIdentifiers, async (assertionIdentifiers: string[]) => {
          await db.assertion.where("identifier").anyOf(assertionIdentifiers)
            .modify(item => {
              item.syncStatus = SyncableStatus.Completed;
              item.updatedAt = new Date(new Date(item.updatedAt).getTime() + 1);
            });
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:Issues");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:Issues", issueIdentifiers, async (issueIdentifiers: string[]) => {
          await db.assertionIssue.where("identifier").anyOf(issueIdentifiers)
            .modify(item => {
              item.syncStatus = SyncableStatus.Completed;
              item.updatedAt = new Date(new Date(item.updatedAt).getTime() + 1);
            });
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:Media");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:Media", mediaIdentifiers, async (mediaIdentifiers: string[]) => {
          const mediaToUpdate = await db.media.where("fileIdentifier").anyOf(mediaIdentifiers)
            .toArray();
          await db.media.bulkUpdate(mediaToUpdate.map(item => ({
            key: item.id!,
            changes: {
              syncStatus: SyncableStatus.Completed,
              updatedAt: new Date(new Date(item.updatedAt).getTime() + 1)
            }
          })));
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:JobIssues");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:JobIssues", jobIssueIdentifiers, async (jobIssueIdentifiers: string[]) => {
          await db.jobIssue.where("identifier").anyOf(jobIssueIdentifiers)
            .modify(item => {
              item.syncStatus = SyncableStatus.Completed;
              item.updatedAt = new Date(new Date(item.updatedAt).getTime() + 1);
            });
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:JobIssuesMedia");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:JobIssuesMedia", jobIssueMediaIdentifiers, async (jobIssueMediaIdentifiers: string[]) => {
          const jobIssueMediaToUpdate = await db.media.where("fileIdentifier").anyOf(jobIssueMediaIdentifiers)
            .toArray();
          await db.media.bulkUpdate(jobIssueMediaToUpdate.map(item => ({
            key: item.id!,
            changes: {
              syncStatus: SyncableStatus.Completed,
              updatedAt: new Date(new Date(item.updatedAt).getTime() + 1)
            }
          })));
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:JobIssueVendors");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:JobIssueVendors", jobIssueVendorIdentifiers, async (jobIssueVendorIdentifiers: string[]) => {
          await db.jobIssueVendor.where("issueVendorIdentifier").anyOf(jobIssueVendorIdentifiers)
            .modify(item => {
              item.syncStatus = SyncableStatus.Completed;
              item.updatedAt = new Date(new Date(item.updatedAt).getTime() + 1);
            });
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:JobSignatures");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:JobSignatures", signatureIdentifiers, async (signatureIdentifiers: string[]) => {
          await db.jobSignature.where("identifier").anyOf(signatureIdentifiers)
            .modify(item => {
              item.syncStatus = SyncableStatus.Completed;
              item.updatedAt = new Date(new Date(item.updatedAt).getTime() + 1);
            });
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:DeletedJobIssueVendors");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:DeletedJobIssueVendors", deletedJobIssueVendorIdentifiers, async (deletedJobIssueVendorIdentifiers: string[]) => {
          // Hard delete the successfully deleted vendors from IndexedDB
          await db.jobIssueVendor
            .where("issueVendorIdentifier")
            .anyOf(deletedJobIssueVendorIdentifiers)
            .delete();

        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:DeletedJobIssueMedia");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:DeletedJobIssueMedia", deletedJobIssueMediaIdentifiers, async (deletedJobIssueMediaIdentifiers: string[]) => {
          await db.media
            .where("fileIdentifier")
            .anyOf(deletedJobIssueMediaIdentifiers)
            .and(media => media.deleted === 1)
            .delete();
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:DeletedUserVendorContacts");
        await this.updateDatabaseEntityAsBatch<UserVendorContactIdentifier>("UpdateDatabaseEntities:DeletedUserVendorContacts", deletedUserVendorContactIdentifiers, async (deletedUserVendorContactIdentifiers: UserVendorContactIdentifier[]) => {
          for (const deletedContact of deletedUserVendorContactIdentifiers) {
            await db.userVendorContact
              .where("[externalVendorId+externalFinancialCommunityId+email]")
              .equals([
                deletedContact.externalVendorId,
                deletedContact.externalFinancialCommunityId,
                deletedContact.email
              ])
              .delete();
          }
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:DeletedJobIssues");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:DeletedJobIssues", deletedJobIssueIdentifiers, async (deletedJobIssueIdentifiers: string[]) => {
          await db.jobIssue
            .where("identifier")
            .anyOf(deletedJobIssueIdentifiers)
            .and(issue => issue.deleted === 1)
            .delete();
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:DeletedIssues");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:DeletedIssues", deletedAssertionIssueIdentifiers, async (deletedAssertionIssueIdentifiers: string[]) => {
          await db.assertionIssue
            .where("identifier")
            .anyOf(deletedAssertionIssueIdentifiers)
            .and((issue: AssertionIssue) => issue.deleted === 1)
            .delete();
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:DeletedMedia");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:DeletedMedia", deletedAssertionMediaIdentifiers, async (deletedAssertionMediaIdentifiers: string[]) => {
          await db.media
            .where("fileIdentifier")
            .anyOf(deletedAssertionMediaIdentifiers)
            .and(media => media.deleted === 1)
            .delete();
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:LotAssetMedia");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:LotAssetMedia", lotAssetMediaIdentifiers, async (lotAssetMediaIdentifiers: string[]) => {
          const lotAssetMediaToUpdate = await db.media.where("fileIdentifier").anyOf(lotAssetMediaIdentifiers)
            .toArray();
          await db.media.bulkUpdate(lotAssetMediaToUpdate.map(item => ({
            key: item.id!,
            changes: {
              syncStatus: SyncableStatus.Completed,
              updatedAt: new Date(new Date(item.updatedAt).getTime() + 1)
            }
          })));

          const syncedLotIds = Array.from(new Set(lotAssetMediaToUpdate.map(item => Number(item.parentIdentifier))));
          if (syncedLotIds.length > 0) {
            await db.lot.where("id").anyOf(syncedLotIds)
              .modify({ syncStatus: SyncableStatus.Completed });
          }
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:DeletedLotAssetMedia");
        await this.updateDatabaseEntityAsBatch<string>("UpdateDatabaseEntities:DeletedLotAssetMedia", deletedLotAssetMediaIdentifiers, async (deletedLotAssetMediaIdentifiers: string[]) => {
          await db.media
            .where("fileIdentifier")
            .anyOf(deletedLotAssetMediaIdentifiers)
            .and(media => media.deleted === 1)
            .delete();
        });

        this.checkForSyncAbort("updateDatabaseEntityAsBatch:UserVendorContacts");
        await this.updateDatabaseEntityAsBatch<UserVendorContactIdentifier>("UpdateDatabaseEntities:UserVendorContacts", contactResultSet, async (contactResultSet: UserVendorContactIdentifier[]) => {
          await db.userVendorContact
            .where("[externalVendorId+externalFinancialCommunityId+email]")
            .anyOf(contactResultSet.map(i => [
              i.externalVendorId,
              i.externalFinancialCommunityId,
              i.email
            ]))
            .modify(item => {
              item.syncStatus = SyncableStatus.Completed;
              item.updatedAt = new Date(new Date(item.updatedAt).getTime() + 1);
            });
        });

      } catch (err: unknown) {
        this.logger.error(err as Error);
      } finally {
        this.logger.event("DataSync:updateSyncedDatabaseEntitiesWithResultsCompleted", { jobIds: updatedIdentifiers?.jobIds ?? [] });
        this.logger.timeEnd("DataSync.UpdateSyncedDatabaseEntitiesWithResults");
        db.setSuppressHooks(false);
      }
    });
  }

  // Reports progress for one apply-phase entity type (e.g. "UpdateDatabaseEntities:Assertions"),
  // then re-derives the overall "apply" phase progress as the average of every active type's own
  // ratio, weighted equally regardless of its batch count.
  private updateApplyPhaseProgress(entityType: string, completed: number, total: number): void {
    this.applyEntityProgress.set(entityType, { completed, total });

    const activeEntries = Array.from(this.applyEntityProgress.values()).filter(entry => entry.total > 0);
    if (activeEntries.length === 0) {
      this.syncStatusService.updatePhaseProgress("apply", 0, 1);
      return;
    }

    const averageRatio = activeEntries.reduce((sum, entry) => sum + (entry.completed / entry.total), 0) / activeEntries.length;
    this.syncStatusService.updatePhaseProgress("apply", averageRatio, 1);
  }

  private async updateDatabaseEntityAsBatch<T extends string | number | UserVendorContactIdentifier>(progressIdentifier: string, identifiers: T[], updateCallback: (identifiers: T[]) => Promise<void>) {
    // Real per-entity-type totals are now known - drop the upfront estimate placeholder so it
    // doesn't keep counting as an extra (dead) type in the equally-weighted average.
    this.applyEntityProgress.delete("EstimatedApplyTotal");

    const identifierBatches = identifiers ? Array.from({ length: Math.ceil(identifiers.length / DATABASE_UPDATE_BATCH_SIZE) }, (_, i) => identifiers.slice(i * DATABASE_UPDATE_BATCH_SIZE, i * DATABASE_UPDATE_BATCH_SIZE + DATABASE_UPDATE_BATCH_SIZE)) : [];
    this.updateApplyPhaseProgress(progressIdentifier, 0, identifierBatches.length);

    for (let i = 0; i < identifierBatches.length; i++) {
      await updateCallback(identifierBatches[i]);
      this.syncStatusService.recordCompletedStep(`${progressIdentifier}:Batch${i + 1}`);
      this.updateApplyPhaseProgress(progressIdentifier, i + 1, identifierBatches.length);
    }
  }

  // Helper function to merge arrays in sync results
  mergeSyncResults(accumulated: AccumulatedResults, newData: SyncResults) {
    const result: AccumulatedResults = { ...accumulated, ...newData };

    // List of all array properties that need merging
    const arrayProperties = Object.keys(accumulated) as (keyof AccumulatedResults)[];

    // Merge each array property
    arrayProperties.forEach((prop: keyof AccumulatedResults) => {
      const key = prop;
      const dataKey = prop as keyof SyncResults;
      const mergedData = [
        ...(accumulated[key] || []),
        ...(newData[dataKey] || [])
      ] as UserVendorContactIdentifier[] & string[] & number[];

      result[key] = mergedData;
    });

    return result;
  }

  // private async performAbortSync(abortSyncStep: string) {
  //   this.logger.info(`[DataSync] Sync aborted at step: ${abortSyncStep}`);
  //   this.stopSyncTimeout();
  //   this.syncStatusService.markSyncProgressItemComplete("DataSync:TotalSyncProcess");
  //   this.shouldAbortSync = false;

  //   return Promise.resolve(false);
  // }

  private checkForSyncAbort(abortSyncStep: string) {
    if (this.shouldAbortSync) {
      this.logger.infoWithTrace(`[DataSync] Sync aborted at step: ${abortSyncStep}`);
      this.stopSyncTimeout();
      this.syncStatusService.markSyncProgressItemComplete("DataSync:TotalSyncProcess");
      this.shouldAbortSync = false;

      const syncAbortError = new Error(`Data sync aborted at step: ${abortSyncStep}`);
      syncAbortError.name = CONSTANTS.ERRORS.SYNC_ABORTED;

      throw syncAbortError;
    }
  }

  abortSync() {
    this.shouldAbortSync = true;
  }
}
